import { httpsCallable } from "firebase/functions";
import { collection, doc, getDoc, getDocs, query, where, limit } from "firebase/firestore";

import { db, functions } from "../firebase";
import {
  loyaltyBonusFromBalance,
  daysUntilMonthEnd,
  creditCapForRole,
  SPEND,
} from "../constants/creditsConfig";

const dailyCheckInFn = httpsCallable(functions, "dailyCheckIn");
const nominateCreatorFn = httpsCallable(functions, "nominateCreator");
const unlockDropWithCreditsFn = httpsCallable(functions, "unlockDropWithCredits");
const sendWallNoteFn = httpsCallable(functions, "sendWallNote");
const createStarterPackCheckoutFn = httpsCallable(functions, "createStarterPackCheckout");
const createTipCheckoutFn = httpsCallable(functions, "createTipCheckoutSession");
const confirmTipPaymentFn = httpsCallable(functions, "confirmTipPayment");
const getSavedTipCardFn = httpsCallable(functions, "getSavedTipCard");
const getTipJarFillFn = httpsCallable(functions, "getCreatorTipJarFill");

function callableError(err) {
  return err?.message || err?.details || "Request failed.";
}

export async function performDailyCheckIn() {
  const { data } = await dailyCheckInFn();
  return data;
}

export async function nominateCreator(creatorId) {
  try {
    const { data } = await nominateCreatorFn({ creatorId });
    return data;
  } catch (err) {
    throw new Error(callableError(err));
  }
}

export async function unlockDropWithCredits(dropId) {
  try {
    const { data } = await unlockDropWithCreditsFn({ dropId });
    return data;
  } catch (err) {
    throw new Error(callableError(err));
  }
}

export async function sendWallNote(creatorId, text) {
  try {
    const { data } = await sendWallNoteFn({ creatorId, text });
    return data;
  } catch (err) {
    throw new Error(callableError(err));
  }
}

export async function createStarterPackCheckout({ successUrl, cancelUrl } = {}) {
  try {
    const { data } = await createStarterPackCheckoutFn({ successUrl, cancelUrl });
    return data;
  } catch (err) {
    throw new Error(callableError(err));
  }
}

export async function createTipCheckout({
  creatorId,
  amount,
  anonymous,
  dropId,
  quickPay = false,
}) {
  try {
    const { data } = await createTipCheckoutFn({
      creatorId,
      amount,
      anonymous,
      dropId,
      quickPay,
    });
    return data;
  } catch (err) {
    throw new Error(callableError(err));
  }
}

export async function confirmTipPayment({ paymentIntentId }) {
  try {
    const { data } = await confirmTipPaymentFn({ paymentIntentId });
    return data;
  } catch (err) {
    throw new Error(callableError(err));
  }
}

export async function fetchSavedTipCard() {
  try {
    const { data } = await getSavedTipCardFn();
    return data?.card || null;
  } catch {
    return null;
  }
}

export async function fetchTipJarFill(creatorId) {
  const { data } = await getTipJarFillFn({ creatorId });
  return data;
}

export async function hasNominatedToday(creatorId, userId) {
  if (!creatorId || !userId) return false;
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "America/Toronto" });
  const nomId = `${creatorId}_${userId}_${today}`;
  const snap = await getDoc(doc(db, "nominations", nomId));
  return snap.exists();
}

export async function hasSentWallNoteThisMonth(creatorId, userId) {
  if (!creatorId || !userId) return false;
  const month = new Date().toLocaleDateString("en-CA", {
    timeZone: "America/Toronto",
    year: "numeric",
    month: "2-digit",
  }).slice(0, 7);
  const limitId = `${creatorId}_${userId}_${month}`;
  const snap = await getDoc(doc(db, "wallNotes", limitId));
  return snap.exists();
}

export function nominationCost(isCreator = false) {
  return isCreator ? SPEND.nomination.creator : SPEND.nomination.fan;
}

export function wallNoteCost(isCreator = false) {
  return isCreator ? SPEND.wallNote.creator : SPEND.wallNote.fan;
}

export async function fetchCreditSummary(userId, isCreator = false) {
  const perksSnap = await getDoc(doc(db, "userPerks", userId));
  const perks = perksSnap.exists ? perksSnap.data() : {};
  const balance = perks.credits || 0;
  const cap = creditCapForRole(isCreator);
  const loyaltyPreview = loyaltyBonusFromBalance(balance);
  const daysLeft = daysUntilMonthEnd();

  return {
    balance,
    cap,
    loyaltyPreview,
    daysUntilReset: daysLeft,
    starterPackPurchased: Boolean(perks.starterPackPurchased),
    currentStreak: perks.currentStreak || perks.streak || 0,
    monthlyCheckInCredits: perks.monthlyCheckInCredits || 0,
    monthlyFreeUnlocks: perks.monthlyFreeUnlocks || 0,
    freeUnlocksRemaining: Math.max(
      SPEND.freeUnlock.monthlyCap - (perks.monthlyFreeUnlocks || 0),
      0
    ),
  };
}

export async function fetchMonthlyEarningsBreakdown(userId) {
  const monthPrefix = new Date().toLocaleDateString("en-CA", {
    timeZone: "America/Toronto",
    year: "numeric",
    month: "2-digit",
  }).slice(0, 7);

  const txSnap = await getDocs(
    query(
      collection(db, `creditHistory/${userId}/transactions`),
      limit(200)
    )
  );

  const breakdown = {
    checkIns: 0,
    drops100: 0,
    nominations: 0,
    wallNotes: 0,
    tips: 0,
    other: 0,
  };

  txSnap.docs.forEach((d) => {
    const tx = d.data();
    if (!tx.createdAt) return;
    const ts = tx.createdAt?.seconds ? tx.createdAt.seconds * 1000 : 0;
    const txMonth = new Date(ts).toLocaleDateString("en-CA", {
      timeZone: "America/Toronto",
      year: "numeric",
      month: "2-digit",
    }).slice(0, 7);
    if (txMonth !== monthPrefix || (tx.amount || 0) <= 0) return;

    switch (tx.type) {
      case "daily_check_in":
        breakdown.checkIns += tx.amount;
        break;
      case "drop_100_views":
        breakdown.drops100 += tx.amount;
        break;
      case "nomination_received":
        breakdown.nominations += tx.amount;
        break;
      case "wall_note_received":
        breakdown.wallNotes += tx.amount;
        break;
      case "tip_bonus":
        breakdown.tips += tx.amount;
        break;
      default:
        breakdown.other += tx.amount;
        break;
    }
  });

  return breakdown;
}

export async function fetchCreatorDropMilestoneProgress(userId) {
  const dropsSnap = await getDocs(
    query(collection(db, "drops"), where("creatorId", "==", userId), limit(50))
  );
  const count = dropsSnap.size;
  const nextMilestone = count < 10 ? 10 : count < 100 ? 100 : null;
  const remaining = nextMilestone ? Math.max(nextMilestone - count, 0) : 0;
  return { dropCount: count, nextMilestone, remaining };
}
